import * as React from 'react'
import { Search, X, ChevronDown, Calendar, Check, EyeOff, Eye, Gauge, Tag } from 'lucide-react'

export type StockDatePreset = 'last30' | 'last90' | 'last180' | 'ytd' | 'custom'
export type StockTypeFilter = 'all' | 'product' | 'medication'

interface Props {
  search: string
  onSearchChange: (v: string) => void
  datePreset: StockDatePreset
  onDatePresetChange: (v: StockDatePreset) => void
  customFrom: string
  customTo: string
  onCustomFromChange: (v: string) => void
  onCustomToChange: (v: string) => void
  type: StockTypeFilter
  onTypeChange: (v: StockTypeFilter) => void
  coverageDays: number
  onCoverageDaysChange: (v: number) => void
  demo: boolean
  onDemoChange: (v: boolean) => void
}

const DATE_OPTIONS: { value: StockDatePreset; label: string }[] = [
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'last180', label: 'Last 180 days' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'custom', label: 'Custom...' },
]
const TYPE_OPTIONS: { value: StockTypeFilter; label: string }[] = [
  { value: 'all', label: 'All stockables' },
  { value: 'product', label: 'Products' },
  { value: 'medication', label: 'Medications' },
]
const COVERAGE_OPTIONS = [30, 45, 60, 90]

function Pill({
  open,
  onToggle,
  icon,
  label,
  width,
  children,
}: {
  open: boolean
  onToggle: () => void
  icon?: React.ReactNode
  label: React.ReactNode
  width: string
  children: React.ReactNode
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 ${width} border border-border transition-all hover:bg-hover`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <span className="text-[13px] text-text-dark truncate">{label}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={onToggle} />
          <div className="absolute left-0 top-full mt-1 min-w-[190px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
            {children}
          </div>
        </>
      )}
    </div>
  )
}

export function StockToolbar(props: Props) {
  const [panel, setPanel] = React.useState<string | null>(null)
  const toggle = (k: string) => setPanel(panel === k ? null : k)
  const dateLabel = DATE_OPTIONS.find((o) => o.value === props.datePreset)?.label ?? 'Window'
  const typeLabel = TYPE_OPTIONS.find((o) => o.value === props.type)?.label ?? 'All'

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {/* search */}
      <div className="flex items-center gap-2 bg-card rounded-full px-3.5 py-2 w-full sm:w-[240px] border border-border focus-within:ring-2 focus-within:ring-info/20">
        <Search className="w-4 h-4 text-foreground flex-shrink-0" />
        <input
          type="text"
          placeholder="Search SKU..."
          value={props.search}
          onChange={(e) => props.onSearchChange(e.target.value)}
          className="border-none outline-none text-[13px] w-full bg-transparent text-text-dark placeholder:text-text-muted"
        />
        {props.search && (
          <button onClick={() => props.onSearchChange('')} className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-hover">
            <X className="w-3.5 h-3.5 text-text-muted" />
          </button>
        )}
      </div>

      {/* date window */}
      <Pill open={panel === 'date'} onToggle={() => toggle('date')} icon={<Calendar className="w-4 h-4 text-foreground" />} label={dateLabel} width="w-[150px]">
        {DATE_OPTIONS.map((o) => (
          <button
            key={o.value}
            onClick={() => {
              props.onDatePresetChange(o.value)
              if (o.value !== 'custom') setPanel(null)
            }}
            className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
              props.datePreset === o.value ? 'bg-hover text-text-dark font-medium' : 'text-text-dark hover:bg-hover'
            }`}
          >
            {o.label}
            {props.datePreset === o.value && <Check className="w-3.5 h-3.5 text-success" />}
          </button>
        ))}
        {props.datePreset === 'custom' && (
          <div className="border-t border-border mt-1 pt-2 px-2 pb-1 space-y-2">
            <div>
              <label className="text-[10px] uppercase text-text-muted">From</label>
              <input type="date" value={props.customFrom} onChange={(e) => props.onCustomFromChange(e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-[12px]" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-text-muted">To</label>
              <input type="date" value={props.customTo} onChange={(e) => props.onCustomToChange(e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-[12px]" />
            </div>
          </div>
        )}
      </Pill>

      {/* type */}
      <Pill open={panel === 'type'} onToggle={() => toggle('type')} icon={<Tag className="w-4 h-4 text-foreground" />} label={typeLabel} width="w-[150px]">
        {TYPE_OPTIONS.map((o) => (
          <button
            key={o.value}
            onClick={() => {
              props.onTypeChange(o.value)
              setPanel(null)
            }}
            className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
              props.type === o.value ? 'bg-hover text-text-dark font-medium' : 'text-text-dark hover:bg-hover'
            }`}
          >
            {o.label}
            {props.type === o.value && <Check className="w-3.5 h-3.5 text-success" />}
          </button>
        ))}
      </Pill>

      {/* coverage assumption */}
      <Pill open={panel === 'coverage'} onToggle={() => toggle('coverage')} icon={<Gauge className="w-4 h-4 text-foreground" />} label={`${props.coverageDays}d coverage`} width="w-[150px]">
        <div className="px-2.5 py-1.5 text-[11px] text-text-muted">Even-stocking target</div>
        {COVERAGE_OPTIONS.map((d) => (
          <button
            key={d}
            onClick={() => {
              props.onCoverageDaysChange(d)
              setPanel(null)
            }}
            className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
              props.coverageDays === d ? 'bg-hover text-text-dark font-medium' : 'text-text-dark hover:bg-hover'
            }`}
          >
            {d} days
            {props.coverageDays === d && <Check className="w-3.5 h-3.5 text-success" />}
          </button>
        ))}
      </Pill>

      {/* privacy mode */}
      <button
        onClick={() => props.onDemoChange(!props.demo)}
        className={`flex items-center gap-2 rounded-full px-3.5 py-2 border text-[12px] transition-all ${
          props.demo ? 'bg-info/10 text-info border-info/30' : 'bg-card text-text-muted border-border hover:bg-hover'
        }`}
        title="Anonymise SKU names + hide RM for external presentations"
      >
        {props.demo ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        {props.demo ? 'Privacy mode ON' : 'Privacy mode'}
      </button>
    </div>
  )
}
